import styled from '@emotion/styled';
import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { PanelState } from '@racemap/utilities/consts/events';
import {
  getCurrentNumberOfDaysOfEventCycle,
  getNumberOfFreeItems,
} from '@racemap/utilities/functions/billing';
import {
  eventIsNeitherFreeNorPaid,
  isChild,
  isGroupEvent,
} from '@racemap/utilities/functions/utils';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Flex } from 'antd';
import type { Immutable } from 'immer';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useEventBasePrice } from 'src/lib/customHooks/useEventBasePrice';
import { usePriceList } from 'src/lib/customHooks/usePriceList';
import { formatPrice } from 'src/misc/formatPrice';
import { useUser } from '../../../lib/customHooks';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { useStore } from '../../../store/reducers';
import { BasicPanel } from '../../BasicComponents/BasicPanel/BasicPanel';
import { IconShoppingCart } from '../../BasicComponents/Icons';
import { LoadingIndicator } from '../../BasicComponents/LoadingIndicator';

interface Props {
  status: PanelState;
}

const numFor = new Intl.NumberFormat('en', { notation: 'standard' });

export const CheckoutPanel = ({ status }: Props) => {
  const event = useCurrentEvent();
  const eventBasePrice = useEventBasePrice(event?.type);
  const { user: creator } = useUser({ userId: event?.creatorId });
  const successState = status === PanelState.SUCCESS;
  const currentUser = useCurrentUser();
  const daysOfBillingCycle = getCurrentNumberOfDaysOfEventCycle();
  const { priceList, isLoading: isLoadingPriceList } = usePriceList({
    isReseller: creator?.isReseller,
  });
  const { openPaymentModal } = useStore((s) => s.events.actions);

  if (isLoadingPriceList) return <LoadingIndicator />;
  if (
    event == null ||
    currentUser == null ||
    successState ||
    eventBasePrice == null ||
    priceList == null
  )
    return null;
  const freeGpsDevices = getNumberOfFreeItems(StripeProducts.GPS_DEVICE, priceList);
  const freeTransponders = getNumberOfFreeItems(StripeProducts.TRANSPONDER, priceList);
  const freeMapLoads = getNumberOfFreeItems(StripeProducts.MAP_OR_MONITOR_LOAD, priceList);
  const freeLeaderboardLoads = getNumberOfFreeItems(
    StripeProducts.API_OR_LEADERBOARD_LOAD,
    priceList,
  );

  return (
    <Section>
      <BasicPanel title="Checkout">
        <Container>
          <Flex vertical>
            {!isChild(currentUser) && <Price>{formatPrice(eventBasePrice)}</Price>}
            <ul>
              <li>Show your event public without watermark.</li>
              <li>
                Up to <strong>{numFor.format(daysOfBillingCycle)}</strong> days event running time.
              </li>
              <li>
                Up to <strong>{numFor.format(freeGpsDevices)}</strong>&nbsp;GPS devices &{' '}
                <strong>{numFor.format(freeTransponders)}</strong>&nbsp;transponders per account and
                month included.
              </li>
              <li>
                Up to <strong>{numFor.format(freeMapLoads)}</strong>&nbsp;map loads &{' '}
                <strong>{numFor.format(freeLeaderboardLoads)}</strong>
                &nbsp;API loads per account per month included.
              </li>
            </ul>
          </Flex>
          <Flex vertical justify="center" align="center">
            <ActivationButton
              event={event}
              currentUser={currentUser}
              onClick={() => openPaymentModal()}
            />
          </Flex>
        </Container>
      </BasicPanel>
    </Section>
  );
};

const ActivationButton = ({
  event,
  currentUser,
  onClick,
}: {
  event: Immutable<CurrentEvent>;
  currentUser: Immutable<User_Legacy>;
  onClick: () => void;
}) => {
  const canActivate = userCanActivate(event, currentUser);
  const handleClick = () => {
    if (!canActivate) return;

    onClick();
  };

  if (!eventIsNeitherFreeNorPaid(event) || isGroupEvent(event)) return null;

  return (
    <Button
      onClick={handleClick}
      disabled={!canActivate}
      title={!canActivate ? 'Only the owner of the Tracking Map can pay for it!' : ''}
    >
      <Icon>
        <IconShoppingCart />
      </Icon>
      <div className="button-title">Activate your Event</div>
    </Button>
  );
};

const userCanActivate = (event: Immutable<CurrentEvent>, currentUser: Immutable<User_Legacy>) => {
  return currentUser?.id === event.creatorId || currentUser?.parentId != null;
};

const Price = styled.div`
  margin-bottom: 15px;
  text-align: center; 
  font-size: x-large;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 2em;
`;

const Button = styled.div<{ disabled: boolean }>`
  border: solid 2px ${RacemapColors.PaleBlue};
  background-color: ${RacemapColors.LightBlue};
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  border-radius: 5px;
  backdrop-filter: blur(10px);
  margin: 2px;
  max-height: 110px;
  min-width: 300px;
  cursor: pointer;
  color: ${RacemapColors.PaleBlue};
  padding: 5px;

  .button-title {
    font-size: 1.3em;
    font-weight: 500;
    color: ${RacemapColors.PaleBlue};
    margin-bottom: 3px;
  }

  .icon,
  svg {
    height: 35px;
  }

  :hover {
    border-color: ${RacemapColors.CloudBlue};
    background-color: ${`${RacemapColors.PaleBlue}88`};
    box-shadow: 0 0 6px 1px ${RacemapColors.PaleBlue};
    color: white;

    .button-title {
      color: white;
    }

    .icon,
    svg {
      filter: brightness(1000);
    }
  }
`;

const Icon = styled.div`
  margin: 5px;
  height: 40px;
  display: flex;
  align-items: center;
`;

const Section = styled.div`
  padding: 1em 0;
  border: none;
`;
